import "./Contact.css";
import { SITEKEY } from "../../config/baseurl";
import { SubmitHandler, useForm } from "react-hook-form";
import { createMessage } from "../../api/message";
import { verifyRecaptcha } from "../../api/recaptcha";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

interface FormFields {
  title: string;
  name: string;
  email: string;
  message: string;
  honeypot: string;
}
function Contact() {
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [status, setStatus] = useState<
    "not-executed" | "loading" | "success" | "error"
  >("not-executed");
  const [error, setError] = useState<string | null>(null);
  const pendingData = useRef<FormFields | null>(null);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormFields>();

  const submitMessage = async (data: FormFields, token: string) => {
    setStatus("loading");
    try {
      await verifyRecaptcha({ recaptchaToken: token });
      await createMessage(data);
      setStatus("success");
      reset();
      setShowRecaptcha(false);
      pendingData.current = null;
    } catch {
      setError("Failed to send message. Please try again.");
      setStatus("error");
    }
  };

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    setError(null);

    if (data.honeypot) {
      setError("Spam detected.");
      setStatus("error");
      return;
    }

    pendingData.current = data;
    setShowRecaptcha(true);
  };

  const onRecaptchaChange = (token: string | null) => {
    if (token && pendingData.current) {
      setShowRecaptcha(false);
      submitMessage(pendingData.current, token);
    }
  };

  return (
    <>
      <section id="contact" className="homepage-section">
        <div className="contact-container">
          <h1 className="contact-title">Contact Me</h1>
          <form className="contact-form" onSubmit={handleSubmit(onSubmit)}>
            <input
              type="text"
              {...register("title", { required: "Title is required" })}
              placeholder="Title"
              aria-label="Message title"
            />
            {errors.title && (
              <div className="error-message" role="alert">{errors.title.message}</div>
            )}
            <input
              type="text"
              {...register("name", { required: "Name is required" })}
              placeholder="Full Name"
              aria-label="Full name"
            />
            {errors.name && (
              <div className="error-message" role="alert">{errors.name.message}</div>
            )}
            <input
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              })}
              placeholder="Email"
              aria-label="Email address"
            />
            {errors.email && (
              <div className="error-message" role="alert">{errors.email.message}</div>
            )}
            <textarea
              id="message"
              {...register("message", { required: "Message is required" })}
              placeholder="Message"
              rows={6}
              aria-label="Message"
            />
            {errors.message && (
              <div className="error-message" role="alert">{errors.message.message}</div>
            )}
            {/* Honeypot field */}
            <input
              type="text"
              {...register("honeypot")}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            {showRecaptcha && (
              <ReCAPTCHA
                sitekey={SITEKEY}
                onChange={onRecaptchaChange}
              />
            )}
            <button type="submit" disabled={status === "loading" || showRecaptcha}>
              {status === "loading" ? "SENDING..." : "SEND"}
            </button>
            {status === "success" && (
              <div className="success-message" role="status">Message sent successfully!</div>
            )}
            {status === "error" && <div className="error-message" role="alert">{error}</div>}
          </form>
        </div>
      </section>
    </>
  );
}

export default Contact;
